import { useState, useCallback } from 'react';
import {
  callLogsApi,
  surveysApi,
  documentsApi,
  ordersApi,
} from '../services/api';
import { formatDateToLocal } from '../utils/timesheetRules';
import { toNaiveLocal } from '../utils/visitTime';
import type {
  CallLogCreate,
  SurveyResponseSubmit,
  OrderCreate,
} from '../services/api';
import type { SurveyResponseEntry } from '../utils/visitDB';

export interface VisitData {
  callLogId: number | null;
  checkInTime: Date | null;
  checkOutTime: Date | null;
  uploadedFiles: { id: string; type: 'photo' | 'document'; file: File }[];
  orderItems: { productId: number; quantity: number; notes?: string }[];
  signature: { managerName: string; signatureDataUrl: string } | null;
  notes: string;
}

interface GPSLocation {
  lat: number;
  lng: number;
  accuracy?: number;
}

// Drive-to-store data captured when closing a drive segment at check-in
export interface DriveToStoreData {
  startTime: Date;
  endTime: Date;
  durationMinutes: number;
  startOdometer?: number;
  endOdometer?: number;
}

interface UseStoreVisitResult {
  visitData: VisitData;
  isLoading: boolean;
  error: string | null;

  // Actions
  checkIn: (storeId: number, projectId: number, associateId: number, gps?: GPSLocation, overrideDate?: string, gpsOverrideReason?: string, driveData?: DriveToStoreData) => Promise<number | null>;
  checkOut: (callLogId: number, gps?: GPSLocation) => Promise<boolean>;
  submitSurvey: (surveyId: number, callLogId: number, responses: SurveyResponseEntry[], urgentReview?: boolean) => Promise<boolean>;
  uploadFiles: (projectId: number, storeId: number, callLogId?: number) => Promise<boolean>;
  submitOrder: (projectId: number, storeId: number, associateId: number) => Promise<boolean>;
  captureSignature: (managerName: string, signatureDataUrl: string) => void;
  uploadSignature: () => Promise<boolean>;
  updateNotes: (notes: string) => void;
  resetVisit: () => void;
}

const initialVisitData: VisitData = {
  callLogId: null,
  checkInTime: null,
  checkOutTime: null,
  uploadedFiles: [],
  orderItems: [],
  signature: null,
  notes: '',
};

/**
 * Idempotency key generator. crypto.randomUUID is available in every
 * PWA-supporting browser; the fallback exists only to keep types happy.
 * Same pattern as ManualEntryPreflight (#1867).
 */
function generateIdempotencyKey(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }
  return `chk-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function useStoreVisit(): UseStoreVisitResult {
  const [visitData, setVisitData] = useState<VisitData>(initialVisitData);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Idempotency key for the check-in API call (#1873 — Rob's onsite
  // bug at Publix 886). When the rep double-/triple-taps "Check In with
  // GPS" or the UI silently reloads mid-network, the same key prevents
  // the server from creating duplicate CallLog rows. The key is stable
  // for the lifetime of this hook instance — i.e., for one mount of
  // StoreDetail. Rotated only after a successful check-in so the next
  // visit attempt gets a fresh key.
  const [checkInKey, setCheckInKey] = useState<string>(() => generateIdempotencyKey());

  // Check in to store - creates call log entry
  // overrideDate: optional YYYY-MM-DD string for retroactive entries
  // gpsOverrideReason: optional reason when checking in outside GPS tolerance
  // driveData: optional drive-to-store data captured from the preceding drive segment
  const checkIn = useCallback(
    async (storeId: number, projectId: number, associateId: number, gps?: GPSLocation, overrideDate?: string, gpsOverrideReason?: string, driveData?: DriveToStoreData): Promise<number | null> => {
      setIsLoading(true);
      setError(null);

      try {
        const now = new Date();
        // Naive local wall-clock recorded to the minute — the calllog
        // serializer's contract. A retroactive entry keeps its overridden DATE
        // while the time still comes from the clock.
        const formatTimeToISO = (date: Date, overrideDay?: string) => {
          const naive = toNaiveLocal(date.toISOString());
          return overrideDay ? `${overrideDay}T${naive.slice(11)}` : naive;
        };
        const callDateISO = overrideDate ? formatTimeToISO(now, overrideDate) : now.toISOString();
        const callTimeIn = formatTimeToISO(now, overrideDate);

        const callLogData: CallLogCreate = {
          store_id: storeId,
          project_id: projectId,
          associate_id: associateId,
          dtecall: callDateISO,
          stcalltime: callTimeIn,
          teamproject: false,
          teamleader: false,
          // Include GPS coordinates if available (skip for retroactive entries)
          // Round to 6 decimal places to fit backend max_digits=10 constraint
          ...(!overrideDate && gps && {
            lat: Math.round(gps.lat * 1000000) / 1000000,
            lon: Math.round(gps.lng * 1000000) / 1000000,
            gps_accuracy: gps.accuracy != null ? Math.round(gps.accuracy * 100) / 100 : undefined,
          }),
          // Include GPS override reason if provided
          ...(gpsOverrideReason && { gps_override_reason: gpsOverrideReason }),
          // Include drive-to-store data if available
          ...(driveData && {
            stdrivetime: formatTimeToISO(driveData.startTime, overrideDate),
            eddrivetime: formatTimeToISO(driveData.endTime, overrideDate),
            driveodostart: driveData.startOdometer,
            driveodoend: driveData.endOdometer,
          }),
        };

        const callLogResponse = await callLogsApi.createCallLog(callLogData, { idempotencyKey: checkInKey });
        // API wraps response: {status, message, data: {calllogid, ...}}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const callLog = (callLogResponse as any).data || callLogResponse;
        const callLogId = callLog.calllogid;

        setVisitData((prev) => ({
          ...prev,
          callLogId,
          checkInTime: now,
        }));
        // Rotate the idempotency key so a subsequent visit attempt in
        // the same hook lifetime doesn't get folded into this CallLog.
        setCheckInKey(generateIdempotencyKey());

        return callLogId;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to check in';
        setError(message);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [checkInKey]
  );

  // Check out of store - updates call log with end time
  const checkOut = useCallback(
    async (callLogId: number, _gps?: GPSLocation): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        const now = new Date();
        const callTimeOut = now.toTimeString().slice(0, 5); // HH:MM format

        // Build full datetime string for edcalltime (checkout time)
        const todayStr = formatDateToLocal(now);
        const callTimeOutFull = `${todayStr}T${callTimeOut}:00`;

        await callLogsApi.updateCallLog(callLogId, {
          edcalltime: callTimeOutFull,
          notes: visitData.notes || undefined,
          // GPS could be used for checkout location tracking in future
          // For now, primary GPS capture is at check-in
        });

        setVisitData((prev) => ({
          ...prev,
          checkOutTime: now,
        }));

        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to check out';
        setError(message);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [visitData.notes]
  );

  // Submit survey responses. Responses are passed in explicitly — canonical
  // state lives in useVisitTask's IDB-backed store, not here. See #1948.
  const submitSurvey = useCallback(
    async (
      surveyId: number,
      callLogId: number,
      responses: SurveyResponseEntry[],
      urgentReview = false,
    ): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        const submitData: SurveyResponseSubmit = {
          surveyid: surveyId,
          calllogid: callLogId,
          responses,
          urgentReview,
        };

        await surveysApi.submitSurveyResponse(submitData);
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to submit survey';
        setError(message);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  // Upload files (photos and documents)
  const uploadFiles = useCallback(
    async (projectId: number, storeId: number, callLogId?: number): Promise<boolean> => {
      if (visitData.uploadedFiles.length === 0) return true;

      setIsLoading(true);
      setError(null);

      try {
        const photos = visitData.uploadedFiles
          .filter((f) => f.type === 'photo')
          .map((f) => f.file);
        const documents = visitData.uploadedFiles
          .filter((f) => f.type === 'document')
          .map((f) => f.file);

        const today = formatDateToLocal(new Date());

        if (photos.length > 0) {
          await documentsApi.uploadPhoto(projectId, storeId, photos, callLogId, today);
        }

        if (documents.length > 0) {
          await documentsApi.uploadDocument(projectId, storeId, documents, callLogId, today);
        }

        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to upload files';
        setError(message);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [visitData.uploadedFiles]
  );

  // Submit order
  const submitOrder = useCallback(
    async (projectId: number, storeId: number, associateId: number): Promise<boolean> => {
      if (visitData.orderItems.length === 0) return true;

      setIsLoading(true);
      setError(null);

      try {
        const orderData: OrderCreate = {
          projectid: projectId,
          storeid: storeId,
          associateid: associateId,
          products: visitData.orderItems.map((item) => ({
            productid: item.productId,
            quantity: item.quantity,
            notes: item.notes,
          })),
        };

        await ordersApi.createOrder(orderData);
        return true;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to submit order';
        setError(message);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [visitData.orderItems]
  );

  // Capture signature locally
  const captureSignature = useCallback((managerName: string, signatureDataUrl: string) => {
    setVisitData((prev) => ({
      ...prev,
      signature: { managerName, signatureDataUrl },
    }));
  }, []);

  // Upload signature to server
  const uploadSignature = useCallback(async (): Promise<boolean> => {
    if (!visitData.signature) return true;

    setIsLoading(true);
    setError(null);

    try {
      // Queue-only write (#1987): the sync queue is the SOLE server-write path.
      const { writeOps } = await import('../sync/writeOps');
      await writeOps.uploadProfileSignature.apply({
        signatureDataUrl: visitData.signature.signatureDataUrl,
      });
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to upload signature';
      setError(message);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [visitData.signature]);

  // Update notes
  const updateNotes = useCallback((notes: string) => {
    setVisitData((prev) => ({ ...prev, notes }));
  }, []);

  // Reset visit data
  const resetVisit = useCallback(() => {
    setVisitData(initialVisitData);
    setError(null);
  }, []);

  return {
    visitData,
    isLoading,
    error,
    checkIn,
    checkOut,
    submitSurvey,
    uploadFiles,
    submitOrder,
    captureSignature,
    uploadSignature,
    updateNotes,
    resetVisit,
  };
}
